import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { BASE_URL, API_URLS } from '../constants/config';
import { checkIfTokenExpired, getTokensFromStore } from './tokens';
import { refreshTokens } from '../actions/usersActions';
import { getStoredChampId } from './championships';
import store from './store';

export const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (data && typeof data === 'object') {
    // Серверные 400 часто: { err: '...', code: 'NEED_TEAMS' } или { error: '...' }
    if (typeof data.err === 'string' && data.err.trim()) return data.err.trim();
    if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
    if (typeof data.error?.message === 'string' && data.error.message.trim()) return data.error.message.trim();
    if (typeof data.error?.err === 'string' && data.error.err.trim()) return data.error.err.trim();
    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
  }
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return fallback;
};

/** Prefer structured API payload when axios stored it (or on Error.message). */
export const getApiErrorPayload = (error) => {
  const data = error?.response?.data;
  if (data && typeof data === 'object' && !(typeof Blob !== 'undefined' && data instanceof Blob)) {
    return data;
  }
  return null;
};

export const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => {
    const result = String(reader.result || '');
    resolve(result.split(',')[1] || result);
  };
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const readTokenPayload = () => {
  const tokens = getTokensFromStore();
  if (!tokens?.access_token) return {};
  try {
    const decoded = jwtDecode(tokens.access_token);
    const nested = decoded.decode_data || decoded.decoded_data || {};
    return { ...decoded, ...nested };
  } catch (error) {
    return {};
  }
};

export const getChampId = () => {
  const stored = getStoredChampId();
  if (stored) return stored;
  const payload = readTokenPayload();
  const user = store.getState()?.user?.userData?.data || {};
  const value = payload.champ_id
    || payload.championat_id
    || payload.default_champ_id
    || user.champ_id
    || user.championat_id
    || user.default_champ_id;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const authorizedPost = async (dispatch, url, body = {}) => {
  if (checkIfTokenExpired()) {
    await dispatch(refreshTokens());
  }
  const tokens = getTokensFromStore();
  if (!tokens?.access_token) {
    const error = new Error('Пользователь не авторизован');
    error.response = { status: 401, data: 'Пользователь не авторизован' };
    throw error;
  }

  return axios.post(BASE_URL.url + url, body, {
    headers: {
      Authorization: 'Bearer ' + tokens.access_token,
      'Content-Type': 'application/json',
    },
  });
};

export const loadChampionshipOptions = async (dispatch) => {
  const fromToken = getChampId();
  const options = [];
  const seen = new Set();

  const addOption = (id, name) => {
    const numericId = Number(id);
    if (!Number.isFinite(numericId) || numericId <= 0 || seen.has(numericId)) return;
    seen.add(numericId);
    options.push({
      value: numericId,
      name: name || `Чемпионат ${numericId}`,
    });
  };

  if (checkIfTokenExpired()) {
    await dispatch(refreshTokens());
  }
  const tokens = getTokensFromStore();
  if (tokens?.access_token) {
    try {
      const groupedResponse = await axios.get(BASE_URL.url + API_URLS.getChampionshipsGroupedByCity, {
        headers: {
          Authorization: 'Bearer ' + tokens.access_token,
          Accept: 'application/json',
        },
      });
      const groups = Array.isArray(groupedResponse.data)
        ? groupedResponse.data
        : groupedResponse.data?.data || [];
      groups.forEach((city) => {
        (city.championships || []).forEach((championship) => {
          addOption(championship.id, championship.name);
        });
      });
    } catch (error) {
      // fall through to leagues
    }

    if (!options.length) {
      try {
        const leaguesResponse = await axios.get(BASE_URL.url + API_URLS.getAllAdminLeagues, {
          headers: {
            Authorization: 'Bearer ' + tokens.access_token,
            Accept: 'application/json',
          },
        });
        const leagues = Array.isArray(leaguesResponse.data)
          ? leaguesResponse.data
          : leaguesResponse.data?.data || [];
        leagues.forEach((league) => {
          addOption(
            league.championat_id || league.championatId || league.championship_id,
            league.championat_name || league.championatName || league.championship_name
          );
        });
      } catch (error) {
        // token champ_id may still work
      }
    }
  }

  if (fromToken && !seen.has(fromToken)) {
    options.unshift({ value: fromToken, name: `Чемпионат ${fromToken}` });
  }

  return { champId: fromToken, options };
};
